import React from "react";
import { motion } from "framer-motion";
import { Users, SuitcaseRolling, CheckCircle, Check, Star } from "@phosphor-icons/react";

export const VehicleCard = ({ v, image, meta, selected, onSelect, t }) => {
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(v.id)}
      data-testid={`vehicle-card-${v.id}`}
      initial={false}
      animate={{ scale: selected ? 1.02 : 1, y: selected ? -4 : 0 }}
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      className={`cfg group relative text-left rounded-[24px] overflow-hidden bg-white border transition-all duration-300 ${
        selected
          ? "border-[#D4AF37] shadow-[0_0_0_2px_#D4AF37,0_30px_70px_-25px_rgba(212,175,55,0.5)]"
          : "border-[#071B2F]/8 shadow-[0_20px_50px_-30px_rgba(7,27,47,0.4)] hover:shadow-[0_30px_70px_-30px_rgba(7,27,47,0.5)]"
      }`}
    >
      {/* Badge */}
      <span className="absolute top-4 left-4 z-10 cfg-body text-[10px] font-semibold tracking-wider uppercase px-3 py-1.5 rounded-full bg-[#071B2F] text-[#D4AF37]">
        {v.badge}
      </span>

      {/* Selected check */}
      {selected && (
        <span className="absolute top-4 right-4 z-10 cfg-check w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-lg" data-testid={`vehicle-check-${v.id}`}>
          <Check weight="bold" size={18} className="text-white" />
        </span>
      )}

      {/* Image */}
      <div className="relative h-52 bg-gradient-to-b from-[#F4F5F7] to-white overflow-hidden">
        <img src={image} alt={v.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="cfg-display text-2xl text-[#071B2F] leading-tight">{v.name}</h3>
            <p className="cfg-body text-[12px] text-[#071B2F]/45 mt-0.5">{v.model}{v.year ? ` · ${v.year}` : ""}</p>
          </div>
          <span className="cfg-body text-sm tracking-widest text-[#D4AF37] shrink-0 mt-1">
            {"€".repeat(meta.price)}<span className="text-[#071B2F]/15">{"€".repeat(4 - meta.price)}</span>
          </span>
        </div>
        <p className="cfg-body text-[13px] text-[#071B2F]/55 mt-2 min-h-[40px]">{v.desc}</p>

        {(v.power || v.tier) && (
          <div className="flex flex-wrap gap-2 mt-3">
            {v.power && <span className="cfg-body text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#F4F5F7] text-[#071B2F]/70">{v.power}</span>}
            {v.tier && <span className={`cfg-body text-[11px] font-semibold px-2.5 py-1 rounded-full ${v.tier === "VIP" ? "bg-[#071B2F] text-[#D4AF37]" : "bg-[#D4AF37]/12 text-[#a9862a]"}`}>{v.tier}</span>}
          </div>
        )}

        <div className="flex items-center gap-5 mt-4 text-[#071B2F]/70">
          <span className="flex items-center gap-1.5 cfg-body text-[13px]"><Users weight="fill" size={16} className="text-[#D4AF37]" />{meta.passengers}</span>
          <span className="flex items-center gap-1.5 cfg-body text-[13px]"><SuitcaseRolling weight="fill" size={16} className="text-[#D4AF37]" />{meta.luggage}</span>
          <span className="flex items-center gap-1 cfg-body text-[13px] ml-auto">
            {[...Array(5)].map((_, i) => <Star key={i} weight="fill" size={11} className={i < meta.comfort ? "text-[#D4AF37]" : "text-[#071B2F]/12"} />)}
          </span>
        </div>

        <div className="mt-5 pt-5 border-t border-[#071B2F]/8 space-y-2">
          {v.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2 cfg-body text-[13px] text-[#071B2F]/75">
              <CheckCircle weight="fill" size={15} className="text-[#D4AF37] shrink-0" />{f}
            </div>
          ))}
        </div>

        <div className={`mt-6 font-bookman text-center text-[13px] font-bold tracking-widest uppercase py-3 rounded-2xl transition-colors ${
          selected ? "bg-[#D4AF37] text-white" : "bg-[#F4F5F7] text-[#071B2F] group-hover:bg-[#071B2F] group-hover:text-white"
        }`}>
          {selected ? t.selected : t.select}
        </div>
      </div>
    </motion.button>
  );
};
